import {
  consumedFeatureIds,
  DocumentError,
  parseDocument,
  type Feature,
  type HomecadDocument,
  type Vec3,
} from "./document.js";
import {
  evaluateExpression,
  isValidParameterName,
  referencedNames,
  renameInExpression,
  resolveParameters,
  type Expression,
} from "./expression.js";

/**
 * Les operations d'edition du document. Elles vivent ici, avec le schema, et
 * non dans l'interface : ce sont elles qui garantissent qu'un document reste
 * valide, et elles doivent pouvoir se verifier sans navigateur ni OpenCascade.
 *
 * Chaque operation rend un **nouveau** document deja valide, ou jette une
 * `DocumentError` en laissant l'original intact. L'appelant n'a donc jamais a
 * verifier lui-meme ce qu'il vient d'ecrire : soit il recoit un document bon,
 * soit il recoit un message a afficher.
 */

export type FieldKind = "expression" | "vec3" | "flag" | "choix" | "body" | "bodies";

export interface FeatureField {
  /** Nom de la propriete dans le document. */
  key: string;
  /** Libelle affiche a l'utilisateur. */
  label: string;
  kind: FieldKind;
  /** Valeur implicite quand la propriete est absente du document. */
  fallback?: Vec3;
  /** Les valeurs possibles d'un champ `choix`, et comment les nommer. */
  options?: readonly { readonly valeur: string; readonly label: string }[];
}

/** Les operations d'une booleenne, dans l'ordre ou l'interface les propose. */
const OPERATIONS = [
  { valeur: "union", label: "Union" },
  { valeur: "subtract", label: "Soustraction" },
  { valeur: "intersect", label: "Intersection" },
] as const;

const LIAISONS = [
  { valeur: "tourillons", label: "Tourillons" },
  { valeur: "aucune", label: "Aucune" },
] as const;

/**
 * La table des champs editables, par type de feature. Elle sert deux fois : a
 * l'interface, qui dessine les champs sans rien savoir des types, et a la
 * validation, qui doit retrouver toutes les expressions d'une feature. Une
 * seule table, donc un champ ajoute au schema ne peut pas etre oublie d'un
 * cote et present de l'autre.
 */
const FIELDS: Record<Feature["type"], readonly FeatureField[]> = {
  box: [
    { key: "width", label: "Largeur (X)", kind: "expression" },
    { key: "depth", label: "Profondeur (Y)", kind: "expression" },
    { key: "height", label: "Hauteur (Z)", kind: "expression" },
    { key: "origin", label: "Origine", kind: "vec3", fallback: [0, 0, 0] },
    { key: "centered", label: "Centrée sur son origine", kind: "flag" },
  ],
  cylinder: [
    { key: "radius", label: "Rayon", kind: "expression" },
    { key: "height", label: "Hauteur", kind: "expression" },
    { key: "origin", label: "Origine", kind: "vec3", fallback: [0, 0, 0] },
    { key: "axis", label: "Axe", kind: "vec3", fallback: [0, 0, 1] },
  ],
  sphere: [
    { key: "radius", label: "Rayon", kind: "expression" },
    { key: "origin", label: "Origine", kind: "vec3", fallback: [0, 0, 0] },
  ],
  boolean: [
    { key: "op", label: "Opération", kind: "choix", options: OPERATIONS },
    { key: "targetId", label: "Cible", kind: "body" },
    { key: "toolIds", label: "Outils", kind: "bodies" },
  ],
  transform: [
    { key: "targetId", label: "Cible", kind: "body" },
    { key: "translate", label: "Translation", kind: "vec3", fallback: [0, 0, 0] },
    { key: "rotate", label: "Rotation (°)", kind: "vec3", fallback: [0, 0, 0] },
  ],
  decoupe: [
    { key: "targetId", label: "Corps à débiter", kind: "body" },
    { key: "plateau", label: "Plateau (mm)", kind: "vec3" },
    { key: "marge", label: "Marge de plateau", kind: "expression" },
    { key: "liaisons", label: "Liaisons", kind: "choix", options: LIAISONS },
    { key: "tourillonDiametre", label: "Tourillon ⌀", kind: "expression" },
    { key: "tourillonLongueur", label: "Tourillon longueur", kind: "expression" },
    { key: "tourillonJeu", label: "Tourillon jeu", kind: "expression" },
    { key: "paroiMini", label: "Paroi mini autour", kind: "expression" },
  ],
};

/**
 * Le plateau propose a un debit neuf : celui de la machine d'ici, une Creality
 * K2 Plus. Ce n'est qu'un defaut — chaque document porte le sien, et changer
 * celui-ci ne touche a aucune piece deja ecrite.
 */
export const PLATEAU_PAR_DEFAUT = 350;

/** Les libelles de creation, dans l'ordre ou l'interface les propose. */
export const FEATURE_TYPES: readonly { type: Feature["type"]; label: string }[] = [
  { type: "box", label: "Boîte" },
  { type: "cylinder", label: "Cylindre" },
  { type: "sphere", label: "Sphère" },
  { type: "boolean", label: "Booléenne" },
  { type: "transform", label: "Transformation" },
  { type: "decoupe", label: "Débit" },
];

export function featureFields(type: Feature["type"]): readonly FeatureField[] {
  return FIELDS[type];
}

function asRecord(feature: Feature): Record<string, unknown> {
  return feature as unknown as Record<string, unknown>;
}

function message(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/** Toutes les expressions d'une feature, avec de quoi nommer la fautive. */
function featureExpressions(feature: Feature): { label: string; expression: Expression }[] {
  const found: { label: string; expression: Expression }[] = [];
  const record = asRecord(feature);
  for (const field of FIELDS[feature.type]) {
    const value = record[field.key];
    if (value === undefined) continue;
    if (field.kind === "expression") {
      found.push({ label: field.label, expression: value as Expression });
    } else if (field.kind === "vec3") {
      const axes = ["X", "Y", "Z"];
      (value as Vec3).forEach((component, index) => {
        found.push({ label: `${field.label} ${axes[index]}`, expression: component });
      });
    }
  }
  return found;
}

/**
 * Le controle complet : la forme, l'ordre des references, et la resolution de
 * toutes les expressions. `parseDocument` ne fait que les deux premiers — une
 * cote citant un parametre efface le passe sans broncher et n'echoue qu'au
 * calcul, loin du geste qui l'a cassee.
 *
 * La geometrie, elle, n'est pas controlee ici : un rayon nul est une erreur de
 * l'evaluateur, pas du document. Le serveur ne calcule jamais de geometrie, et
 * ce module doit pouvoir tourner des deux cotes.
 */
export function validateDocument(candidate: HomecadDocument): HomecadDocument {
  const document = parseDocument(candidate);

  let scope: Record<string, number>;
  try {
    scope = resolveParameters(document.parameters);
  } catch (cause) {
    throw new DocumentError(`Parametres : ${message(cause)}`);
  }

  for (const feature of document.features) {
    for (const { label, expression } of featureExpressions(feature)) {
      try {
        evaluateExpression(expression, scope);
      } catch (cause) {
        throw new DocumentError(`« ${feature.name} », ${label} : ${message(cause)}`);
      }
    }
  }
  return document;
}

/**
 * Lit une saisie utilisateur en expression. Un nombre reste un nombre : le
 * document garde `40` et non `"40"`, ce qui le laisse lisible et evite deux
 * ecritures de la meme cote — donc deux empreintes et deux calculs.
 */
export function parseExpressionInput(input: string): Expression {
  const texte = input.trim();
  if (texte === "") throw new DocumentError("Une cote ne peut pas etre vide");
  if (texte.length > 200) throw new DocumentError("Expression trop longue");
  const nombre = Number(texte);
  return Number.isFinite(nombre) ? nombre : texte;
}

/** Un nom libre parmi ceux deja pris : `base`, puis `base 2`, `base 3`… */
function uniqueName(base: string, taken: Iterable<string>, separator: string): string {
  const pris = new Set(taken);
  if (!pris.has(base)) return base;
  for (let n = 2; ; n += 1) {
    const candidat = `${base}${separator}${n}`;
    if (!pris.has(candidat)) return candidat;
  }
}

/**
 * Les corps qu'aucune feature ne consomme : ce que le document produit
 * reellement. Sert a proposer une cible par defaut a une booleenne.
 */
export function availableBodies(document: HomecadDocument): Feature[] {
  const consumed = new Set<string>();
  for (const feature of document.features) {
    for (const id of consumedFeatureIds(feature)) consumed.add(id);
  }
  return document.features.filter(
    (feature) => feature.suppressed !== true && !consumed.has(feature.id),
  );
}

/** Les corps qu'une feature a le droit de citer : ceux places au-dessus d'elle. */
export function citableFeatures(document: HomecadDocument, featureId: string): Feature[] {
  const index = document.features.findIndex((feature) => feature.id === featureId);
  return index < 0 ? [] : document.features.slice(0, index);
}

function requireFeature(document: HomecadDocument, featureId: string): Feature {
  const feature = document.features.find((candidate) => candidate.id === featureId);
  if (feature === undefined) throw new DocumentError(`Feature inconnue : ${featureId}`);
  return feature;
}

/**
 * Remplace des proprietes d'une feature. Le document repasse par zod, qui
 * rejette ce qui ne tient pas et ecarte au passage une clef inconnue : le
 * detour par un enregistrement non type ne peut donc pas laisser passer une
 * feature malformee.
 */
function patchFeature(
  document: HomecadDocument,
  featureId: string,
  patch: Record<string, unknown>,
): HomecadDocument {
  const features = document.features.map((feature) =>
    feature.id === featureId
      ? ({ ...asRecord(feature), ...patch } as unknown as Feature)
      : feature,
  );
  return validateDocument({ ...document, features });
}

// --- Parametres ---------------------------------------------------------

export function setParameterExpression(
  document: HomecadDocument,
  name: string,
  expression: Expression,
): HomecadDocument {
  if (!document.parameters.some((parameter) => parameter.name === name)) {
    throw new DocumentError(`Parametre inconnu : ${name}`);
  }
  return validateDocument({
    ...document,
    parameters: document.parameters.map((parameter) =>
      parameter.name === name ? { ...parameter, expression } : parameter,
    ),
  });
}

export function addParameter(document: HomecadDocument): HomecadDocument {
  const name = uniqueName(
    "parametre",
    document.parameters.map((parameter) => parameter.name),
    "_",
  );
  return validateDocument({
    ...document,
    parameters: [...document.parameters, { name, expression: 10 }],
  });
}

/** Ce qui cite un parametre : les autres parametres, et les cotes des features. */
function parameterUsages(document: HomecadDocument, name: string): string[] {
  const usages: string[] = [];
  for (const parameter of document.parameters) {
    if (parameter.name === name) continue;
    if (referencedNames(parameter.expression).includes(name)) usages.push(parameter.name);
  }
  for (const feature of document.features) {
    const cite = featureExpressions(feature).some(({ expression }) =>
      referencedNames(expression).includes(name),
    );
    if (cite) usages.push(`« ${feature.name} »`);
  }
  return usages;
}

export function removeParameter(document: HomecadDocument, name: string): HomecadDocument {
  if (!document.parameters.some((parameter) => parameter.name === name)) {
    throw new DocumentError(`Parametre inconnu : ${name}`);
  }
  const usages = parameterUsages(document, name);
  if (usages.length > 0) {
    throw new DocumentError(`${name} est encore utilise par ${usages.join(", ")}`);
  }
  return validateDocument({
    ...document,
    parameters: document.parameters.filter((parameter) => parameter.name !== name),
  });
}

/** Renomme un parametre partout : la table et toutes les cotes qui le citent. */
export function renameParameter(
  document: HomecadDocument,
  from: string,
  to: string,
): HomecadDocument {
  if (from === to) return document;
  if (!document.parameters.some((parameter) => parameter.name === from)) {
    throw new DocumentError(`Parametre inconnu : ${from}`);
  }
  if (!isValidParameterName(to)) {
    throw new DocumentError(`Nom de parametre invalide : ${to}`);
  }
  if (document.parameters.some((parameter) => parameter.name === to)) {
    throw new DocumentError(`Le parametre ${to} existe deja`);
  }

  const parameters = document.parameters.map((parameter) => ({
    ...parameter,
    name: parameter.name === from ? to : parameter.name,
    expression: renameInExpression(parameter.expression, from, to),
  }));
  const features = document.features.map((feature) => renameInFeature(feature, from, to));
  return validateDocument({ ...document, parameters, features });
}

function renameInFeature(feature: Feature, from: string, to: string): Feature {
  const record = { ...asRecord(feature) };
  for (const field of FIELDS[feature.type]) {
    const value = record[field.key];
    if (value === undefined) continue;
    if (field.kind === "expression") {
      record[field.key] = renameInExpression(value as Expression, from, to);
    } else if (field.kind === "vec3") {
      record[field.key] = (value as Vec3).map((component) =>
        renameInExpression(component, from, to),
      ) as Vec3;
    }
  }
  return record as unknown as Feature;
}

// --- Features -----------------------------------------------------------

export function setFeatureField(
  document: HomecadDocument,
  featureId: string,
  key: string,
  value: unknown,
): HomecadDocument {
  const feature = requireFeature(document, featureId);
  if (!FIELDS[feature.type].some((field) => field.key === key)) {
    throw new DocumentError(`« ${feature.name} » n'a pas de champ ${key}`);
  }
  return patchFeature(document, featureId, { [key]: value });
}

export function renameFeature(
  document: HomecadDocument,
  featureId: string,
  name: string,
): HomecadDocument {
  const nom = name.trim();
  if (nom === "") throw new DocumentError("Le nom d'une feature ne peut pas etre vide");
  requireFeature(document, featureId);
  return patchFeature(document, featureId, { name: nom });
}

export function toggleSuppressed(document: HomecadDocument, featureId: string): HomecadDocument {
  const feature = requireFeature(document, featureId);
  return patchFeature(document, featureId, {
    suppressed: feature.suppressed === true ? undefined : true,
  });
}

/**
 * Ajoute une feature en fin de timeline. La fin est la seule position toujours
 * licite : tout corps deja produit y est citable, donc l'ajout ne peut pas
 * casser l'ordre des references. Ce qui doit remonter remonte ensuite.
 */
export function addFeature(document: HomecadDocument, type: Feature["type"]): HomecadDocument {
  const feature = defaultFeature(document, type);
  return validateDocument({ ...document, features: [...document.features, feature] });
}

function defaultFeature(document: HomecadDocument, type: Feature["type"]): Feature {
  const id = uniqueName(
    type,
    document.features.map((feature) => feature.id),
    "_",
  );
  const label = FEATURE_TYPES.find((entry) => entry.type === type)?.label ?? type;
  const name = uniqueName(
    label,
    document.features.map((feature) => feature.name),
    " ",
  );
  const libres = availableBodies(document);

  switch (type) {
    case "box":
      return { id, name, type, width: 10, depth: 10, height: 10 };
    case "cylinder":
      return { id, name, type, radius: 5, height: 10 };
    case "sphere":
      return { id, name, type, radius: 5 };
    case "boolean": {
      // Cible et outil se prennent dans l'ordre de la timeline : la premiere
      // des deux est percee par la seconde, ce qui est le geste courant.
      if (libres.length < 2) {
        throw new DocumentError(
          "Une booleenne demande deux corps libres ; le document n'en a pas assez.",
        );
      }
      const cible = libres[libres.length - 2]!;
      const outil = libres[libres.length - 1]!;
      return { id, name, type, op: "subtract", targetId: cible.id, toolIds: [outil.id] };
    }
    case "transform": {
      if (libres.length < 1) {
        throw new DocumentError("Une transformation demande un corps libre ; il n'y en a aucun.");
      }
      return { id, name, type, targetId: libres[libres.length - 1]!.id, translate: [0, 0, 0] };
    }
    case "decoupe": {
      if (libres.length < 1) {
        throw new DocumentError("Un débit demande un corps libre ; il n'y en a aucun.");
      }
      // Le plateau de la machine d'ici — une Creality K2 Plus, 350 sur les
      // trois axes — et un tourillon de 5. Des cotes qu'on reconnait et qu'on
      // corrige, plutot que des zeros a remplir. La marge de 10 couvre la jupe,
      // l'adherence et le gauchissement : elle laisse 340 utiles.
      return {
        id,
        name,
        type,
        targetId: libres[libres.length - 1]!.id,
        plateau: [PLATEAU_PAR_DEFAUT, PLATEAU_PAR_DEFAUT, PLATEAU_PAR_DEFAUT],
        marge: 10,
        liaisons: "tourillons",
        tourillonDiametre: 5,
        tourillonLongueur: 30,
        tourillonJeu: 0.4,
        paroiMini: 2,
      };
    }
  }
}

/**
 * Supprime une feature, et avec elle ce qui n'a plus de sens sans elle. La
 * regle est celle deja appliquee a `suppressed` par l'evaluateur : une cible
 * qui disparait emporte ce qui la consommait, un outil qui disparait est
 * simplement ecarte tant qu'il en reste un.
 */
export function removeFeature(document: HomecadDocument, featureId: string): HomecadDocument {
  requireFeature(document, featureId);

  const perdues = new Set([featureId]);
  // Une passe avant suffit : une reference ne pointe jamais vers le bas.
  for (const feature of document.features) {
    if (feature.type === "boolean") {
      if (perdues.has(feature.targetId)) perdues.add(feature.id);
      else if (feature.toolIds.every((id) => perdues.has(id))) perdues.add(feature.id);
    } else if (
      (feature.type === "transform" || feature.type === "decoupe") &&
      perdues.has(feature.targetId)
    ) {
      perdues.add(feature.id);
    }
  }

  const features = document.features
    .filter((feature) => !perdues.has(feature.id))
    .map((feature) =>
      feature.type === "boolean"
        ? { ...feature, toolIds: feature.toolIds.filter((id) => !perdues.has(id)) }
        : feature,
    );
  return validateDocument({ ...document, features });
}

/**
 * Deplace une feature d'un cran. Un echange qui ferait citer un corps pas
 * encore produit est refuse par la validation, avec le nom de celui qui doit
 * rester au-dessus — le document n'est pas modifie.
 */
export function moveFeature(
  document: HomecadDocument,
  featureId: string,
  direction: -1 | 1,
): HomecadDocument {
  const index = document.features.findIndex((feature) => feature.id === featureId);
  if (index < 0) throw new DocumentError(`Feature inconnue : ${featureId}`);

  const cible = index + direction;
  if (cible < 0 || cible >= document.features.length) return document;

  const features = [...document.features];
  const deplacee = features[index]!;
  features[index] = features[cible]!;
  features[cible] = deplacee;
  return validateDocument({ ...document, features });
}
